import { useState, useEffect, useCallback } from 'react';
import type { PublicOAuthClient, OAuthScope, OAuthDelegation } from '@runninglime/shared';
import { ALL_OAUTH_SCOPES } from '@runninglime/shared';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../lib/api.ts';
import { LoadingSpinner, EmptyState } from '../../components/ui/Shared.tsx';
import { formatDate } from '../../lib/utils.ts';

// ─── Tab Navigation ──────────────────────────────────────────────────────────

type Tab = 'clients' | 'delegations';

// ─── Scope Categories (for grouping in the UI) ──────────────────────────────

const SCOPE_GROUPS: { label: string; scopes: OAuthScope[] }[] = [
  {
    label: 'Store & Cart',
    scopes: ['store:read', 'cart:read', 'cart:write'],
  },
  {
    label: 'Orders',
    scopes: ['orders:read', 'orders:write'],
  },
  {
    label: 'Tickets',
    scopes: ['tickets:read', 'tickets:write'],
  },
  {
    label: 'Profile',
    scopes: ['profile:read', 'profile:write'],
  },
  {
    label: 'Admin',
    scopes: ['admin:products', 'admin:orders', 'admin:tickets', 'admin:invites', 'admin:users', 'admin:enrollment'],
  },
];

// ─── Main Component ──────────────────────────────────────────────────────────

export default function DeveloperConsole() {
  const [tab, setTab] = useState<Tab>('clients');

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-dark">Developer Console</h1>
        <p className="text-gray-500 mt-1">Manage OAuth2 clients and delegations for chatbot integration</p>
        <div className="mt-1 flex items-center gap-2">
          <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span className="text-xs text-amber-600 font-medium">This section is not accessible via OAuth2 delegation tokens</span>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 shadow-sm w-fit">
        <TabButton active={tab === 'clients'} onClick={() => setTab('clients')} label="OAuth Clients" />
        <TabButton active={tab === 'delegations'} onClick={() => setTab('delegations')} label="Active Delegations" />
      </div>

      {tab === 'clients' && <ClientsTab />}
      {tab === 'delegations' && <DelegationsTab />}
    </div>
  );
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
        active
          ? 'bg-lime/20 text-dark shadow-sm'
          : 'text-gray-500 hover:text-dark hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  );
}

// ─── Clients Tab ─────────────────────────────────────────────────────────────

// Confirmation modal types
type ConfirmAction = 
  | { type: 'delete'; client: PublicOAuthClient }
  | { type: 'regenerate'; client: PublicOAuthClient }
  | { type: 'revoke-delegation'; delegationId: string };

function ConfirmationModal({ action, loading, onConfirm, onCancel }: {
  action: ConfirmAction;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const config = action.type === 'delete'
    ? {
        title: 'Delete Client',
        titleColor: 'text-red-700',
        message: <>Are you sure you want to permanently delete <strong>{action.client.name}</strong>?</>,
        detail: 'All tokens issued to this client will stop working. This action cannot be undone.',
        detailColor: 'text-red-600',
        confirmLabel: 'Delete Permanently',
        confirmClass: 'bg-red-600 text-white hover:bg-red-700',
      }
    : action.type === 'regenerate'
    ? {
        title: 'Regenerate Secret',
        titleColor: 'text-amber-700',
        message: <>Regenerate the secret for <strong>{action.client.name}</strong>?</>,
        detail: 'The old secret will stop working immediately. Make sure to copy the new secret.',
        detailColor: 'text-amber-600',
        confirmLabel: 'Regenerate',
        confirmClass: 'bg-amber-500 text-white hover:bg-amber-600',
      }
    : {
        title: 'Revoke Delegation',
        titleColor: 'text-red-700',
        message: <>Revoke this delegation token?</>,
        detail: 'The token will immediately stop working. The user will need to re-authorize the chatbot.',
        detailColor: 'text-red-600',
        confirmLabel: 'Revoke',
        confirmClass: 'bg-red-600 text-white hover:bg-red-700',
      };

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-scale-in">
        <h3 className={`text-lg font-bold ${config.titleColor} mb-2`}>{config.title}</h3>
        <p className="text-gray-600 text-sm mb-1">{config.message}</p>
        <p className={`${config.detailColor} text-sm mb-4`}>{config.detail}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 font-medium text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors disabled:opacity-50 ${config.confirmClass}`}
          >
            {loading ? 'Processing…' : config.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientsTab() {
  const [clients, setClients] = useState<PublicOAuthClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingClient, setEditingClient] = useState<PublicOAuthClient | null>(null);
  const [newSecret, setNewSecret] = useState<{ clientId: string; secret: string } | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const loadClients = useCallback(async () => {
    const res = await apiGet<PublicOAuthClient[]>('/admin/developer/clients');
    if (res.success && res.data) {
      setClients(res.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleCreated = (client: PublicOAuthClient, secret: string) => {
    setShowCreate(false);
    setNewSecret({ clientId: client.id, secret });
    loadClients();
  };

  const handleToggleActive = async (client: PublicOAuthClient) => {
    const res = await apiPatch<PublicOAuthClient>(`/admin/developer/clients/${client.id}`, {
      active: !client.active,
    });
    if (res.success) await loadClients();
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    setConfirmLoading(true);

    if (confirmAction.type === 'delete') {
      const res = await apiDelete(`/admin/developer/clients/${confirmAction.client.id}`);
      if (res.success) await loadClients();
    } else if (confirmAction.type === 'regenerate') {
      const res = await apiPost<{ client: PublicOAuthClient; clientSecret: string }>(
        `/admin/developer/clients/${confirmAction.client.id}/regenerate-secret`
      );
      if (res.success && res.data) {
        setNewSecret({ clientId: confirmAction.client.id, secret: res.data.clientSecret });
        await loadClients();
      }
    }

    setConfirmAction(null);
    setConfirmLoading(false);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* Secret Display Banner */}
      {newSecret && (
        <SecretBanner
          clientId={newSecret.clientId}
          secret={newSecret.secret}
          onDismiss={() => setNewSecret(null)}
        />
      )}

      {/* Create or Edit Form (inline, never both at once) */}
      {editingClient ? (
        <EditClientModal
          client={editingClient}
          onSaved={() => { setEditingClient(null); loadClients(); }}
          onCancel={() => setEditingClient(null)}
        />
      ) : showCreate ? (
        <CreateClientForm
          onCreated={handleCreated}
          onCancel={() => setShowCreate(false)}
        />
      ) : (
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowCreate(true)}
            className="btn-gradient px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            New Client
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <ConfirmationModal
          action={confirmAction}
          loading={confirmLoading}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Client List */}
      {clients.length === 0 ? (
        <EmptyState
          icon="🔑"
          title="No OAuth Clients"
          description="Create an OAuth2 client to enable chatbot or third-party integrations."
        />
      ) : (
        <div className="space-y-4">
          {clients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onEdit={() => { setShowCreate(false); setEditingClient(client); }}
              onToggleActive={() => handleToggleActive(client)}
              onDelete={() => setConfirmAction({ type: 'delete', client })}
              onRegenerateSecret={() => setConfirmAction({ type: 'regenerate', client })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Secret Banner ───────────────────────────────────────────────────────────

function SecretBanner({ clientId, secret, onDismiss }: {
  clientId: string;
  secret: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6 animate-fade-in">
      <div className="flex items-start gap-3">
        <svg className="w-6 h-6 text-amber-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-amber-800">Client Secret — Copy Now</h3>
          <p className="text-sm text-amber-700 mt-1">
            This secret will <strong>not be shown again</strong>. Store it securely.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 bg-white border border-amber-200 rounded-lg px-3 py-2 text-sm font-mono text-dark break-all select-all">
              {secret}
            </code>
            <button
              onClick={copy}
              className="shrink-0 px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-sm font-medium transition-colors"
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-amber-600 mt-2">
            Client ID: <code className="font-mono">{clientId}</code>
          </p>
        </div>
        <button onClick={onDismiss} className="text-amber-400 hover:text-amber-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Create Client Form ──────────────────────────────────────────────────────

function CreateClientForm({ onCreated, onCancel }: {
  onCreated: (client: PublicOAuthClient, secret: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<OAuthScope[]>([...ALL_OAUTH_SCOPES]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const toggleScope = (scope: OAuthScope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const selectAll = () => setSelectedScopes([...ALL_OAUTH_SCOPES]);
  const selectNone = () => setSelectedScopes([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || selectedScopes.length === 0) return;

    setCreating(true);
    setError('');

    const res = await apiPost<{ client: PublicOAuthClient; clientSecret: string }>(
      '/admin/developer/clients',
      { name: name.trim(), scopes: selectedScopes }
    );

    if (res.success && res.data) {
      onCreated(res.data.client, res.data.clientSecret);
    } else {
      setError(res.error || 'Failed to create client');
    }
    setCreating(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 animate-fade-in">
      <h2 className="text-lg font-semibold text-dark mb-4">Create New OAuth Client</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-dark mb-1.5">Client Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Chatbot Senpai, Inventory Bot"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lime/50 focus:border-lime"
            required
            minLength={2}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-dark">Allowed Scopes</label>
            <div className="flex gap-2">
              <button type="button" onClick={selectAll} className="text-xs text-lime hover:underline">Select All</button>
              <span className="text-gray-300">|</span>
              <button type="button" onClick={selectNone} className="text-xs text-gray-500 hover:underline">Clear</button>
            </div>
          </div>
          <div className="space-y-3">
            {SCOPE_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">{group.label}</p>
                <div className="flex flex-wrap gap-2">
                  {group.scopes.map((scope) => (
                    <ScopeChip
                      key={scope}
                      scope={scope}
                      selected={selectedScopes.includes(scope)}
                      onClick={() => toggleScope(scope)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={creating || !name.trim() || selectedScopes.length === 0}
            className="btn-gradient px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create Client'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Edit Client Modal ───────────────────────────────────────────────────────

function EditClientModal({ client, onSaved, onCancel }: {
  client: PublicOAuthClient;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(client.name);
  const [selectedScopes, setSelectedScopes] = useState<OAuthScope[]>([...client.scopes]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleScope = (scope: OAuthScope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const res = await apiPatch<PublicOAuthClient>(`/admin/developer/clients/${client.id}`, {
      name: name.trim(),
      scopes: selectedScopes,
    });

    if (res.success) {
      onSaved();
    } else {
      setError(res.error || 'Failed to update client');
    }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 animate-fade-in">
      <h2 className="text-lg font-bold text-dark mb-4">Edit Client</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-dark mb-1.5">Client Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lime/50 focus:border-lime"
            required
            minLength={2}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-dark mb-2">Allowed Scopes</label>
          <div className="space-y-3">
            {SCOPE_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">{group.label}</p>
                <div className="flex flex-wrap gap-2">
                  {group.scopes.map((scope) => (
                    <ScopeChip
                      key={scope}
                      scope={scope}
                      selected={selectedScopes.includes(scope)}
                      onClick={() => toggleScope(scope)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-gray-400">
          Client ID: <code className="font-mono">{client.id}</code>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={saving || !name.trim() || selectedScopes.length === 0}
            className="btn-gradient px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Client Card ─────────────────────────────────────────────────────────────

function ClientCard({ client, onEdit, onToggleActive, onDelete, onRegenerateSecret }: {
  client: PublicOAuthClient;
  onEdit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
  onRegenerateSecret: () => void;
}) {
  const [showActions, setShowActions] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  const copyId = async () => {
    await navigator.clipboard.writeText(client.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  return (
    <div className={`bg-white rounded-2xl shadow-sm p-5 transition-all ${!client.active ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="text-lg font-semibold text-dark truncate">{client.name}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              client.active
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {client.active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <code className="font-mono">{client.id}</code>
            <button onClick={copyId} className="hover:text-dark transition-colors" title="Copy Client ID">
              {copiedId ? (
                <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {client.scopes.map((scope) => (
              <span
                key={scope}
                className="inline-block bg-gray-100 text-gray-600 text-[11px] font-medium px-2 py-0.5 rounded-md"
              >
                {scope}
              </span>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">Created {formatDate(client.createdAt)}</p>
        </div>

        {/* Actions menu */}
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-dark bg-gray-100 hover:bg-lime/20 hover:text-dark border border-gray-200 hover:border-lime/30 rounded-xl transition-all"
            title="Actions"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
          {showActions && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)} />
              <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1 animate-fade-in">
                <button
                  onClick={() => { setShowActions(false); onEdit(); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Name & Scopes
                </button>
                <button
                  onClick={() => { setShowActions(false); onRegenerateSecret(); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Regenerate Secret
                </button>
                <button
                  onClick={() => { setShowActions(false); onToggleActive(); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={client.active
                      ? 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636'
                      : 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                    } />
                  </svg>
                  {client.active ? 'Deactivate' : 'Reactivate'}
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => { setShowActions(false); onDelete(); }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Permanently
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Scope Chip ──────────────────────────────────────────────────────────────

function ScopeChip({ scope, selected, onClick }: {
  scope: OAuthScope;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
        selected
          ? 'bg-lime/15 text-dark border-lime/30'
          : 'bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-300'
      }`}
    >
      {selected && (
        <svg className="w-3 h-3 text-lime" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
      {scope}
    </button>
  );
}

// ─── Delegations Tab ─────────────────────────────────────────────────────────

function DelegationsTab() {
  const [delegations, setDelegations] = useState<OAuthDelegation[]>([]);
  const [clients, setClients] = useState<PublicOAuthClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterClientId, setFilterClientId] = useState<string>('');
  const [revoking, setRevoking] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    const [delRes, clientRes] = await Promise.all([
      apiGet<OAuthDelegation[]>(`/admin/developer/delegations${filterClientId ? `?clientId=${filterClientId}` : ''}`),
      apiGet<PublicOAuthClient[]>('/admin/developer/clients'),
    ]);
    if (delRes.success && delRes.data) setDelegations(delRes.data);
    if (clientRes.success && clientRes.data) setClients(clientRes.data);
    setLoading(false);
  }, [filterClientId]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const handleRevoke = async (delegationId: string) => {
    setRevoking(delegationId);
    const res = await apiPost(`/admin/developer/delegations/${delegationId}/revoke`);
    if (res.success) await loadData();
    setRevoking(null);
    setConfirmRevoke(null);
  };

  const getClientName = (clientId: string) =>
    clients.find((c) => c.id === clientId)?.name || clientId;

  const getDelegationStatus = (d: OAuthDelegation): 'active' | 'revoked' | 'expired' => {
    if (d.revoked) return 'revoked';
    if (new Date(d.expiresAt) < new Date()) return 'expired';
    return 'active';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* Filter */}
      {clients.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <label className="text-sm text-gray-500">Filter by client:</label>
          <select
            value={filterClientId}
            onChange={(e) => setFilterClientId(e.target.value)}
            className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime/50"
          >
            <option value="">All Clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}

      {delegations.length === 0 ? (
        <EmptyState
          icon="🔗"
          title="No Delegations Found"
          description={filterClientId ? 'No delegations for this client.' : 'Delegation tokens are created when users authorize a chatbot to act on their behalf.'}
        />
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Delegation ID</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Client</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">User ID</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Expires</th>
                <th className="text-right px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {delegations.map((d) => {
                const status = getDelegationStatus(d);
                return (
                  <tr key={d.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <code className="text-xs font-mono text-gray-500">{d.id.slice(0, 12)}…</code>
                    </td>
                    <td className="px-5 py-3 font-medium text-dark">{getClientName(d.clientId)}</td>
                    <td className="px-5 py-3">
                      <code className="text-xs font-mono text-gray-500">{d.userId.slice(0, 12)}…</code>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        status === 'active'
                          ? 'bg-emerald-50 text-emerald-700'
                          : status === 'revoked'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500">{formatDate(d.createdAt)}</td>
                    <td className="px-5 py-3 text-xs text-gray-500">{formatDate(d.expiresAt)}</td>
                    <td className="px-5 py-3 text-right">
                      {status === 'active' && (
                        <button
                          onClick={() => setConfirmRevoke(d.id)}
                          disabled={revoking === d.id}
                          className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                        >
                          {revoking === d.id ? 'Revoking…' : 'Revoke'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Revoke Delegation Confirmation Modal */}
      {confirmRevoke && (
        <ConfirmationModal
          action={{ type: 'revoke-delegation', delegationId: confirmRevoke }}
          loading={revoking === confirmRevoke}
          onConfirm={() => handleRevoke(confirmRevoke)}
          onCancel={() => setConfirmRevoke(null)}
        />
      )}
    </div>
  );
}
